import React, { useState, Fragment } from 'react';
import {
  Row,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  Input,
  ModalFooter,
  Label,
  Spinner
} from 'reactstrap';
import IntlMessages from "../../../../../helpers/IntlMessages";
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import { Colxx } from '../../../../../components/common/CustomBootstrap';

import { AxiosAPI } from '../../../../../helpers/AxiosAPI';
import { postAPI_Update_Inventory } from '../../../../../constants/APIs';
// Redux
import { injectIntl } from 'react-intl';
import { connect } from 'react-redux';
import moment from 'moment';
import { NotificationManager } from '../../../../../components/common/react-notifications';

// Yup schema for validation
const InventorySchema = Yup.object().shape({
  inputFields: Yup.array().of(
    Yup.object().shape({
      roomTypeId: Yup.string().required("Space type is required"),
      startDate: Yup.date().required("Start date is required"),
      endDate: Yup.date().required("End date is required"),
      timeSlots: Yup.array().of(
        Yup.object().shape({
          timeSlot: Yup.string().required(),
          inventory: Yup.number().min(0, "Inventory must be >= 0").required(),
        })
      )
    })
  )
});

function InventoryEditModal({ isVisible, toggleModal, propertyId, roomlist }) {
  const [loading, setIsLoading] = useState(false);

  const template = { roomTypeId: '', startDate: '', endDate: '', count: 0, timeSlots: [] };

  const handleUpdate = async (values) => {
    setIsLoading(true);
    let updatedData = [];

    values.inputFields.forEach((m) => {
      if (m.roomTypeId !== "") {
        let diff = moment(m.endDate).diff(moment(m.startDate), 'days');
        for (let i = 0; i <= diff; i++) {
          m.timeSlots.forEach((t) => {
            updatedData.push({
              roomTypeId: m.roomTypeId,
              timeSlot: t.timeSlot,
              count: parseInt(t.inventory, 10),
              date: moment(m.startDate).add(i, 'days').format('YYYY-MM-DD')
            });
          });
        }
      }
    });

    try {
      await AxiosAPI({
        url: `${postAPI_Update_Inventory}/meeting-rooms`,
        method: 'post',
        data: { propertyId, data: updatedData },
      });
      NotificationManager.success("", "Availability updated successfully", 3000);
      setTimeout(() => window.location.reload(), 1000);
    } catch (err) {
      console.error(err);
      NotificationManager.error("Error updating inventory");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal size="lg" isOpen={isVisible} toggle={toggleModal}>
      <ModalHeader toggle={toggleModal}>Bulk Edit</ModalHeader>

      <Formik
        initialValues={{ inputFields: [template] }}
        validationSchema={InventorySchema}
        onSubmit={handleUpdate}
      >
        {({ values, setFieldValue }) => (
          <Form>
            <ModalBody>
              {values.inputFields.map((inputField, index) => (
                <Row key={index}>
                  {/* Room Type */}
                  <Colxx sm={4}>
                    <Label>Space Type</Label>
                    <Field
                      as="select"
                      name={`inputFields[${index}].roomTypeId`}
                      className="form-control"
                      onChange={(e) => {
                        const roomTypeId = e.target.value;
                        setFieldValue(`inputFields[${index}].roomTypeId`, roomTypeId);
                        const slotData = roomlist.find(r => r.roomTypeId === roomTypeId);
                        setFieldValue(`inputFields[${index}].timeSlots`, slotData?.data?.[0]?.timeSlot || []);
                      }}
                    >
                      <option value="" disabled hidden>Space Type</option>
                      {roomlist.map((elem, i) => (
                        <option key={i} value={elem.roomTypeId}>{elem.roomName}</option>
                      ))}
                    </Field>
                  </Colxx>

                  {/* Start Date */}
                  <Colxx sm={4}>
                    {inputField.roomTypeId && (
                      <>
                        <Label><IntlMessages id='dashboards.start-date' /></Label>
                        <Field type="date" name={`inputFields[${index}].startDate`} className="form-control" />
                      </>
                    )}
                  </Colxx>

                  {/* End Date */}
                  <Colxx sm={4}>
                    {inputField.roomTypeId && (
                      <>
                        <Label><IntlMessages id='dashboards.end-date' /></Label>
                        <Field type="date" name={`inputFields[${index}].endDate`} className="form-control" />
                      </>
                    )}
                  </Colxx>

                  {/* Time Slots */}
                  {inputField.startDate && inputField.endDate && (
                    <Colxx sm={12}>
                      <Row>
                        <Colxx sm={12}><h4>TimeSlots</h4></Colxx>
                        {inputField.timeSlots?.map((m, slotIdx) => (
                          <Colxx sm={3} key={slotIdx}>
                            <Label>{m.timeSlot}</Label>
                            <Field
                              type="number"
                              min={0}
                              name={`inputFields[${index}].timeSlots[${slotIdx}].inventory`}
                              className="form-control"
                            />
                          </Colxx>
                        ))}
                      </Row>
                    </Colxx>
                  )}

                  {/* Add / Remove buttons */}
                  <Colxx sm={4} className="mb-3">
                    <div>
                      <Button
                        style={{ margin: '6px' }}
                        onClick={() => setFieldValue("inputFields", [...values.inputFields, template])}
                        color="primary"
                        size="xs"
                        outline
                        type="button"
                      >
                        + Add More
                      </Button>
                      {values.inputFields.length > 1 && (
                        <Button
                          style={{ margin: '6px' }}
                          onClick={() => {
                            const copy = [...values.inputFields];
                            copy.splice(index, 1);
                            setFieldValue("inputFields", copy);
                          }}
                          color="secondary"
                          size="xs"
                          outline
                          type="button"
                        >
                          -
                        </Button>
                      )}
                    </div>
                  </Colxx>
                </Row>
              ))}
            </ModalBody>

            <ModalFooter>
              <Button color="primary" outline type="submit">
                {loading ? <Spinner color="primary" /> : "Update"}
              </Button>
              <Button color="secondary" outline onClick={toggleModal}>Cancel</Button>
            </ModalFooter>
          </Form>
        )}
      </Formik>
    </Modal>
  );
}

const mapStateToProps = ({ settings }) => {
  const { propertyId } = settings;
  return { propertyId };
};

export default injectIntl(connect(mapStateToProps, {})(InventoryEditModal));
